import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Trophy, Award, Medal } from 'lucide-react';
import { createTreeSlug } from '../utils/slug';
import ImageWithFallback from './ImageWithFallback';
import toast from 'react-hot-toast';

interface Tree {
  id: string;
  name: string;
  creator: string;
  imageUrl: string;
  votes: number;
}

const getRankInfo = (rank: number) => {
  switch (rank) {
    case 1:
      return {
        icon: Trophy,
        color: 'text-yellow-500',
        shadow: 'shadow-yellow-200',
        label: '1er Lugar'
      };
    case 2:
      return {
        icon: Award,
        color: 'text-gray-400',
        shadow: 'shadow-gray-200',
        label: '2do Lugar'
      };
    case 3:
      return {
        icon: Medal,
        color: 'text-amber-600',
        shadow: 'shadow-amber-200',
        label: '3er Lugar'
      };
    default:
      return {
        icon: Medal,
        color: 'text-gray-400',
        shadow: 'shadow-gray-200',
        label: `${rank}º Lugar`
      };
  }
};

const TreeCard = React.memo(({ tree, rank }: { tree: Tree; rank: number }) => {
  const { icon: RankIcon, color, label } = getRankInfo(rank);

  return (
    <div className="flex-1">
      <Link 
        to={`/arbol/${createTreeSlug(tree.name, tree.id)}`}
        className="block relative group h-full"
      >
        <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow h-full">
          <div className="relative">
            <ImageWithFallback 
              src={tree.imageUrl} 
              alt={tree.name}
              className="w-full h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <p className="text-sm opacity-90">Ver detalles</p>
              </div>
            </div>
          </div>
          
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-lg text-gray-900 truncate">{tree.name}</h3>
              <div className={`flex items-center ${color}`}>
                <RankIcon className="h-5 w-5 mr-1" />
                <span className="text-sm font-medium">{label}</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-2">por {tree.creator}</p>
            <div className="flex items-center text-primary">
              <Trophy className="h-4 w-4 mr-1" />
              <span className="text-sm font-medium">{tree.votes || 0} votos</span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
});

export default function TopTrees() {
  const [trees, setTrees] = useState<Tree[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopTrees = async () => {
      try {
        const treesQuery = query(
          collection(db, 'trees'),
          where('status', '==', 'approved'),
          orderBy('votes', 'desc'),
          limit(5)
        );
        const snapshot = await getDocs(treesQuery);
        const treeData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Tree[];
        setTrees(treeData);
      } catch (error) {
        console.error('Error fetching top trees:', error);
        toast.error('Error al cargar los árboles más votados');
      } finally {
        setLoading(false);
      }
    };

    fetchTopTrees();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-gray-600 mt-4">Cargando los mejores árboles...</p>
        </div>
      </div>
    );
  }

  if (trees.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12 text-primary">
          Top 5 Árboles Más Votados
        </h2>
        <div className="flex flex-col md:flex-row gap-6">
          {trees.map((tree, index) => (
            <TreeCard key={tree.id} tree={tree} rank={index + 1} />
          ))}
        </div>
      </div>
    </div>
  );
}